import noop from 'lodash/noop';
import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { defineWithDefaults } from 'rodash';
import DefaultFieldRenderer from '../field-renderers/DefaultFieldRenderer';
import { ChangeHandler, FieldFactory, FieldFactoryProps, FieldProps, InputElement, TextFieldProps } from '../types';

interface Props<E extends InputElement>
    extends FieldProps<string, E>, TextFieldProps<E> {
    factory: FieldFactory<string, E, FFProps<E>>;
}

type FFProps<E extends InputElement> =
    FieldFactoryProps<string, E> & TextFieldProps<E>;

const withDefaults = defineWithDefaults<Props<InputElement>>({
    Renderer: DefaultFieldRenderer,
    onInputChange: noop,
    onKeyDown: noop,
    onValueChange: noop,
    onValueReset: noop,
    onValueSubmit: noop,
});

const BaseTextField = <E extends InputElement>(props: Props<E>) => {
    props = withDefaults(props);

    const [value, setValue] = useState(props.value);

    const debounceTimeout = useRef(null);

    const useHandleChange = (
        onChange: ChangeHandler
    ) => useCallback((e: ChangeEvent<E>) => {
        const newValue = e.target.value;
        setValue(newValue);
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
            debounceTimeout.current = null;
        }
        if (props.debounce) {
            debounceTimeout.current = setTimeout(() => {
                onChange(newValue, value, props.name, e);
            }, props.debounce || 0);
        } else {
            onChange(newValue, value, props.name, e);
        }
    }, [props.debounce, props.name, onChange, value]);
    const handleChange = useHandleChange(props.onValueChange);
    const handleInput = useHandleChange(props.onInputChange);

    const handleKeyDown = useCallback((e: KeyboardEvent<E>) => {
        props.onKeyDown(e);
        if (e.isDefaultPrevented()) {
            return;
        }
        if (e.key === 'Enter') {
            props.onValueSubmit(value, props.name);
        } else if (e.key === 'Escape') {
            props.onValueReset(value, props.name);
        }
    }, [
        props.name,
        props.onKeyDown,
        props.onValueSubmit,
        props.onValueReset,
        props.value,
    ]);

    useEffect(() => {
        if (value !== props.value) {
            setValue(props.value);
        }
    }, [props.value]);

    return <props.Renderer
        {...props}
        value={value || ''}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onInput={handleInput}
    />;
};

export default BaseTextField;
